import { LitElement, html, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import { styleMap } from "lit-html/directives/style-map"

import { verticalCalculators, horizontalCalculators } from "./position-calculators"

import commonStyle from "../common.css"
import style from "./popup.css"

/**
 * ポップアップ部品
 */
export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			position: { type: String },
			offsetX: { type: Boolean, attribute: "offset-x" },
			offsetY: { type: Boolean, attribute: "offset-y" },
			static: { type: Boolean },
			absolute: { type: Boolean },
			SPShowTop: { type: Boolean }
		}
	}

	/**
	 * @type {boolean} 表示中ならtrue
	 */
	#isShow = false
	/**
	 * @type {boolean} フェードアウトアニメーション中ならtrue
	 */
	#hiding = false
	/**
	 * 表示位置。
	 * 初期表示位置を左上にしておかないと、初期表示時のこのポップアップのサイズ取得(#getThisRect)が上手くいかないケースがある。
	 * @type {"top"|"bottom"|"left"|"right"|"top-left"|"top-right"|"bottom-left"|"bottom-right"} 表示位置
	 */
	#potision = { top: "0px", left: "0px" }
	/** @type {HTMLElement} ポップアップ要素 */
	#popupEl = null
	/** @type {HTMLElement?} モーダル要素。使わない場合null */
	#modal = null

	constructor () {
		super()
		this.position = "bottom-left"
		this.offsetX = false
		this.offsetY = false
		this.static = false
		this.absolute = false
		this.SPShowTop = false
	}

	render () {
		return html`
		${this.static ? html`<bui-modal transparent @modal-hide="${this.hide}"></bui-modal>` : ""}
		<div class=${this.#popupClass} style=${this.#popupStyle}>
			<slot></slot>
		</div>
		`
	}

	firstUpdated () {
		this.#popupEl = this.shadowRoot.querySelector(".popup")
		this.#modal = this.shadowRoot.querySelector("bui-modal")
	}

	#show () {
		this.#isShow = true
		if (this.#modal) this.#modal.show()
		this.requestUpdate()
	}

	showAtTarget ({ target, position, offsetY, offsetX, additionalOffsetY = 0, additionalOffsetX = 0 }) {
		if (position) { this.position = position }
		if (offsetY != null) { this.offsetY = offsetY }
		if (offsetX != null) { this.offsetX = offsetX }
		this.#potision = this.#calcPosition(target, additionalOffsetX, additionalOffsetY)
		this.#show()
	}

	showAtCursor (e) {
		// 表示位置の計算
		const position = {}

		const thisRect = this.#getThisRect()
		const top = e.clientY
		const left = e.clientX
		const isOverlapX = window.innerWidth < thisRect.width + left
		const isOverlapY = window.innerHeight < thisRect.height + top

		if (isOverlapY) {
			position.bottom = "0px"
		} else {
			position.top = `${e.clientY}px`
		}

		if (isOverlapX) {
			position.right = "0px"
		} else {
			position.left = `${e.clientX}px`
		}

		this.#potision = position
		this.#show()
	}

	showAtPoint (x, y) {
		this.#potision = {
			left: `${x}px`,
			top: `${y}px`
		}
		this.#show()
	}

	hide () {
		if (!this.#isShow) return
		if (this.#modal) this.#modal.hide()

		// 非表示アニメーション
		this.#hiding = true
		this.#isShow = false
		this.#popupEl.addEventListener("animationend", () => {
			this.#hiding = false
			this.requestUpdate()
			this.dispatchEvent(new CustomEvent("popup-close", {
				composed: true,
				bubbles: true
			}))
		}, { once: true })
		this.requestUpdate()
	}

	get #popupClass () {
		return classMap({
			popup: true,
			"is-show": this.#isShow,
			"is-hiding": !this.#isShow && this.#hiding,
			"is-SPShowTop": this.SPShowTop
		})
	}

	get #popupStyle () {
		const style = {
			...this.#potision,
			position: this.absolute ? "absolute" : "fixed"
		}
		if (this.#modal && this.#modal.actualZIndex) {
			// モーダルと同じz-index指定
			style["z-index"] = this.#modal.actualZIndex
		} else {
			style["z-index"] = window.bui.getMaxModalZindex() + 1
		}
		return styleMap(style)
	}

	/**
	 *
	 * @param {Event} el
	 * @param {number} additionalOffsetX
	 * @param {number} additionalOffsetY
	 */
	#calcPosition (el, additionalOffsetX, additionalOffsetY) {
		const thisRect = this.#getThisRect()

		let vCalculator
		let hCalculator
		switch (this.position) {
		case "top":
			vCalculator = verticalCalculators.top
			hCalculator = horizontalCalculators.center
			break
		case "top-left":
			vCalculator = verticalCalculators.top
			hCalculator = horizontalCalculators.left
			break
		case "top-right":
			vCalculator = verticalCalculators.top
			hCalculator = horizontalCalculators.right
			break
		case "left":
			vCalculator = verticalCalculators.center
			hCalculator = horizontalCalculators.left
			break
		case "right":
			vCalculator = verticalCalculators.center
			hCalculator = horizontalCalculators.right
			break
		case "bottom":
			vCalculator = verticalCalculators.bottom
			hCalculator = horizontalCalculators.center
			break
		case "bottom-left":
			vCalculator = verticalCalculators.bottom
			hCalculator = horizontalCalculators.left
			break
		case "bottom-right":
			vCalculator = verticalCalculators.bottom
			hCalculator = horizontalCalculators.right
			break
		}
		return {
			...vCalculator.calc({
				anchorEl: el,
				thisRect: thisRect,
				useOffset: this.offsetY,
				additionalOffset: additionalOffsetY
			}),
			...hCalculator.calc({
				anchorEl: el,
				thisRect: thisRect,
				useOffset: this.offsetX,
				additionalOffset: additionalOffsetX
			})
		}
	}

	#getThisRect () {
		// display:noneを一時的に解除して自身のClientRectを取得する.
		const thisEl = this.#popupEl
		const org = thisEl.style.display
		thisEl.style.display = "initial"
		const thisRect = thisEl.getBoundingClientRect()
		thisEl.style.display = org
		return thisRect
	}
}
